import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, NotificationListResponse } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const PAGE_SIZE = 20;
const RECENT_SIZE = 5;

// Every key below stays under the ['notifications'] prefix so the single
// invalidation in useNotificationStream refreshes all of them.

// The bell peek. Kept separate from useUnreadNotificationCount so the
// frequently-polled badge count doesn't force refetching the list.
export const useRecentNotifications = () => {
  const { user, currentOrganization } = useAuth();

  return useQuery<NotificationListResponse>({
    queryKey: ['notifications', 'recent', currentOrganization?.id],
    queryFn: () => api.getNotifications(1, RECENT_SIZE),
    enabled: !!user,
    staleTime: 30 * 1000,
  });
};

export const useNotificationList = (unreadOnly: boolean) => {
  const { user, currentOrganization } = useAuth();

  return useInfiniteQuery({
    queryKey: ['notifications', 'list', currentOrganization?.id, unreadOnly],
    queryFn: ({ pageParam }) => api.getNotifications(pageParam, PAGE_SIZE, unreadOnly),
    initialPageParam: 1,
    getNextPageParam: (last, pages) =>
      pages.length * PAGE_SIZE < last.total ? last.page + 1 : undefined,
    enabled: !!user,
    staleTime: 30 * 1000,
  });
};

export const useUnreadNotificationCount = () => {
  const { user, currentOrganization } = useAuth();

  return useQuery<{ count: number }>({
    queryKey: ['notifications', 'unread-count', currentOrganization?.id],
    queryFn: () => api.getUnreadNotificationCount(),
    enabled: !!user,
    staleTime: 30 * 1000,
  });
};

export const useMarkNotificationRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.markNotificationRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
};

export const useMarkAllNotificationsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.markAllNotificationsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
};
